/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Zalify has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

window.globalData = {};
window.API_URL = "https://api.atomstack-com.workers.dev";
let videoManager;

document.addEventListener("DOMContentLoaded", function () {
  loadObserver();
  initMenuModule();
  initAllVariantSelectors();
  setTimeout(() => {
    if (videoManager) {
      videoManager.destroy();
    }
    videoManager = new ResponsiveVideoManager();
  }, 100);
});
window.addEventListener('beforeunload', function() {
  if (videoManager) {
    videoManager.destroy();
  }
});
class CustomMenu extends HTMLElement {
  constructor() {
    super();
    this.nav = this.querySelector(".custom-menu-nav");
    this.menuItems = this.nav
      ? this.nav.querySelectorAll(".menu-item-wrapper")
      : [];
  }

  connectedCallback() {
    this.menuItems.forEach((menuItem) => {
      menuItem.addEventListener(
        "mouseenter",
        this.handleMenuItemEnter.bind(this)
      );
      menuItem.addEventListener(
        "mouseleave",
        this.handleMenuItemLeave.bind(this)
      );
    });
  }

  handleMenuItemEnter(event) {
    const menuItem = event.currentTarget;
    if (menuItem.querySelector(".submenu")) {
      this.setAttribute("open", "");
    }
  }

  handleMenuItemLeave(event) {
    const menuItem = event.currentTarget;
    if (menuItem.querySelector(".submenu")) {
      this.removeAttribute("open");
    }
  }
}
customElements.define("custom-menu", CustomMenu);

function loadObserver() {
  const observer = lozad(".lozad", {
    loaded: function (el) {
      el.classList.add("loaded");
    },
    threshold: 0.05,
  });
  observer.observe();

  const swiperVideoObserver = lozad(".lazy-video", {
    rootMargin: "50px 0px",
    threshold: 0.1,
    loaded: function (el) {
      el.classList.add("loaded");
      if (el.closest(".swiper-slide-active")) {
        el.play();
      }
    },
  });
  swiperVideoObserver.observe();

  initVideoLoading();
}

function initVideoLoading() {
  const productCards = document.querySelectorAll(".product-card, .ac-product-card");

  productCards.forEach((card) => {
    const videoContainer = card.querySelector(".product-video");
    if (!videoContainer) return;

    const video = videoContainer.querySelector("video");
    if (!video) return;

    const videoSrc = video.getAttribute("data-video-src");

    const playVideo = () => {
      if (videoSrc && !video.src) {
        video.src = videoSrc;
        video.addEventListener("loadeddata", () => {
          videoContainer.classList.add("loaded");
          if (video.readyState >= 2) {
            video.play().catch((e) => console.warn("Video play error:", e));
          }
        });
      } else if (video.readyState >= 2) {
        video.play().catch((e) => console.warn("Video play error:", e));
      }
    };

    card.addEventListener("mouseenter", playVideo);
    card.addEventListener("mouseleave", () => video.pause());
  });
}
function initMenuModule() {
  const listItems = document.querySelectorAll(".b2-menu-module__list-item");
  const contentSections = document.querySelectorAll(
    ".b2-menu-module__content-section"
  );

  if (!listItems.length || !contentSections.length) return;

  listItems[0].classList.add("active");

  listItems.forEach(function (item, index) {
    item.addEventListener("mouseenter", function () {
      const targetIndex = this.getAttribute("data-collection-index");

      listItems.forEach(function (listItem) {
        listItem.classList.remove("active");
      });

      contentSections.forEach(function (section) {
        section.style.display = "none";
      });

      const targetSection = document.querySelector(
        '[data-collection-content="' + targetIndex + '"]'
      );
      if (targetSection) {
        targetSection.style.display = "block";
        this.classList.add("active");
      }
    });
  });
}

// 通用工具函数
const VariantUtils = {
  // 获取产品卡片元素
  getProductCardElements(productCard) {
    if (!productCard) return null;
    
    return {
      priceElement: productCard.querySelector(".product-card__price-current") ||
                   productCard.querySelector(".ac-product-card__price-current") ||
                   productCard.querySelector("div[data-curprice]"),
      oriPriceElement: productCard.querySelector(".ac-product-card__price-original") || 
                      productCard.querySelector("div[data-oriprice]"),
      variantIdInput: productCard.querySelector('.variant-id-input'),
      imageElement: productCard.querySelector('.product-card__image'),
      discountElement: productCard.querySelector('.product-discount'),
      discountTextElement: productCard.querySelector('.product-discount__text')
    };
  },

  // 格式化价格
  formatPrice(price, fallbackText = '') {
    if (!price) return fallbackText;
    
    try {
      if (typeof formatMoneyWithCurrency === 'function') {
        return formatMoneyWithCurrency(price, {enableConversion: false});
      } else {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(price / 100);
      }
    } catch (error) {
      console.warn('Price formatting error:', error);
      return `$${(price / 100).toFixed(2)}`;
    }
  },

  // 计算折扣百分比
  calculateDiscountPercentage(comparePrice, price) {
    if (!comparePrice || !price || isNaN(parseFloat(comparePrice)) || isNaN(parseFloat(price))) {
      return 0;
    }
    
    const comparePriceNum = parseFloat(comparePrice);
    const priceNum = parseFloat(price);
    
    if (comparePriceNum <= priceNum) return 0;
    
    return Math.round(((comparePriceNum - priceNum) / comparePriceNum) * 100);
  },

  // 更新价格显示
  updatePriceDisplay(elements, selectedOption) {
    const { priceElement, oriPriceElement, discountElement, discountTextElement } = elements;
    
    if (!priceElement || !selectedOption) return;

    const price = selectedOption.dataset?.price;
    const comparePrice = selectedOption.dataset?.comparePrice;
    
    if (!price) return;

    // 更新当前价格
    const formattedPrice = this.formatPrice(price);

    // 更新原价显示
    if (oriPriceElement) {
      if (comparePrice && parseFloat(comparePrice) > 0) {
        const formattedComparePrice = this.formatPrice(comparePrice);
        oriPriceElement.textContent = formattedComparePrice;
        oriPriceElement.style.display = 'block';
      } else {
        oriPriceElement.style.display = 'none';
      }
      priceElement.textContent = `${formattedPrice}`;
    } else {
      priceElement.textContent = `From ${formattedPrice}`;
    }
    
    // 更新折扣显示
    const discountPercentage = this.calculateDiscountPercentage(comparePrice, price);
    
    if (discountElement) {
      if (discountPercentage > 0) {
        discountElement.style.display = 'flex';
        if (discountTextElement) {
          discountTextElement.textContent = discountPercentage.toString();
        }
      } else {
        discountElement.style.display = 'none';
      }
    }
  },

  // 更新产品图片
  updateProductImage(imageElement, selectedOption) {
    if (!imageElement || !selectedOption) return;

    const newImage = selectedOption.dataset?.image;
    if (!newImage) return;

    // 添加切换效果
    imageElement.classList?.add('changing');
    
    // 预加载新图片
    const img = new Image();
    
    img.onload = () => {
      // 更新图片源
      imageElement.setAttribute?.('data-src', newImage);
      imageElement.src = newImage;
      
      // 移除切换效果
      setTimeout(() => {
        imageElement.classList?.remove('changing');
      }, 50);
      
      // 如果使用了lozad懒加载，更新相关属性
      if (imageElement.classList?.contains('lozad')) {
        imageElement.setAttribute?.('data-src', newImage);
      }
    };
    
    img.onerror = () => {
      console.warn('Image loading failed:', newImage);
      imageElement.classList?.remove('changing');
    };
    
    img.src = newImage;
  },

  // 更新变体输入
  updateVariantInput(variantIdInput, selectedOption) {
    if (variantIdInput && selectedOption?.value) {
      variantIdInput.value = selectedOption.value;
    }
  }
};

// 全局变体选择器初始化函数
function initAllVariantSelectors() {
  try {
    const allVariantSelectors = document.querySelectorAll('.variant-selector');
    
    if (!allVariantSelectors?.length) return;
    
    allVariantSelectors.forEach(selector => {
      if (!selector) return;
      
      // 移除可能已经存在的事件监听器
      selector.removeEventListener('change', handleGlobalVariantChange);
      // 添加新的事件监听器
      selector.addEventListener('change', handleGlobalVariantChange);
    });
  } catch (error) {
    console.warn('initAllVariantSelectors error:', error);
  }
}

// 全局变体切换处理函数
function handleGlobalVariantChange(event) {
  try {
    const selector = event.target;
    if (!selector?.options) {
      console.warn('Invalid selector in handleGlobalVariantChange');
      return;
    }

    const selectedOption = selector.options[selector.selectedIndex];
    if (!selectedOption) {
      console.warn('No selected option found');
      return;
    }

    const productCard = selector.closest('.product-card') || selector.closest('.ac-product-card');
    if (!productCard) {
      console.warn('Product card not found');
      return;
    }

    const elements = VariantUtils.getProductCardElements(productCard);
    if (!elements) return;
    
    // 更新所有相关元素
    VariantUtils.updatePriceDisplay(elements, selectedOption);
    VariantUtils.updateProductImage(elements.imageElement, selectedOption);
    VariantUtils.updateVariantInput(elements.variantIdInput, selectedOption);

  } catch (error) {
    console.warn('handleGlobalVariantChange error:', error);
  }
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}
function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*"
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}
const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

class ProductTabSlider extends HTMLElement {
  constructor() {
    super();
    
    // 安全地查找元素，防止null错误
    try {
      this.tabNavigation = this.querySelector(".tab-product-card");
      this.tab_button = this.querySelectorAll(".tab-button");
      this.product_slider = this.querySelectorAll(".product-slider");
      this.line_bar = this.querySelector(".line-bar");
      this.swipers = [];
      this.isInitialized = false;
      this.slidesPerView = this.getAttribute("data-slidesPerView");
    } catch (error) {
      console.warn('ProductTabSlider constructor error:', error);
      this.swipers = [];
      this.isInitialized = false;
    }
  }

  connectedCallback() {
    try {
      // 延迟初始化，确保DOM完全加载
      requestAnimationFrame(() => {
        this.initTabNavigation();
        this.initVariantSelectors();
        
        // 安全调用全局函数
        if (typeof initVideoLoading === 'function') {
          initVideoLoading();
        }
      });
    } catch (error) {
      console.warn('ProductTabSlider connectedCallback error:', error);
    }
  }

  initTabNavigation() {
    try {
      // 检查必要元素是否存在
      if (!this.product_slider || this.product_slider.length === 0) {
        console.warn('ProductTabSlider: No product sliders found');
        return;
      }

      // 检查Swiper是否可用
      if (typeof Swiper === 'undefined') {
        console.warn('ProductTabSlider: Swiper library not found');
        return;
      }

      // 初始化每个滑块
      this.product_slider.forEach((content, index) => {
        try {
          const swiperContainer = content.querySelector(".swiper");
          if (!swiperContainer) {
            console.warn(`ProductTabSlider: Swiper container not found for slider ${index}`);
            return;
          }

          const swiper = new Swiper(swiperContainer, {
            slidesPerView: 1,
            spaceBetween: 24,
            speed: 600,
            autoHeight: true,
            watchOverflow: true, // 添加容错配置
            observer: true, // 观察DOM变化
            observeParents: true,
            navigation: {
              nextEl: content.querySelector(".swiper-button-next"),
              prevEl: content.querySelector(".swiper-button-prev"),
              disabledClass: 'swiper-button-disabled',
            },
            pagination: {
              el: content.querySelector(".swiper-progressbar"),
              clickable: true,
              bulletClass: "swiper-pagination-bullet swiper-pagination-bullet-bar",
              bulletActiveClass: "swiper-pagination-bullet-active-bar",
              renderBullet: function (index, className) {
                return '<span class="' + className + '"></span>';
              },
              dynamicBullets: true,
              dynamicMainBullets: 3,
            },
            breakpoints: {
              750: {
                slidesPerView: 1,
              },
              751: {
                slidesPerView: 2,
              },
              1025: {
                slidesPerView: 3,
              },
            },
            on: {
              init: function() {
                console.log(`ProductTabSlider: Swiper ${index} initialized successfully`);
              },
              slideChange: function() {
                // 滑块切换时的容错处理
                try {
                  this.update();
                } catch (error) {
                  console.warn('ProductTabSlider: Swiper update error:', error);
                }
              }
            }
          });
          
          this.swipers.push(swiper);
        } catch (error) {
          console.warn(`ProductTabSlider: Error initializing swiper ${index}:`, error);
        }
      });

      // 初始化线条指示器
      this.initLineBar();
      
      // 初始化标签按钮
      this.initTabButtons();
      
      // 初始化悬停视频
      this.initHoverVideo();
      
      this.isInitialized = true;
      
    } catch (error) {
      console.warn('ProductTabSlider initTabNavigation error:', error);
    }
  }

  initLineBar() {
    try {
      if (!this.line_bar || !this.tab_button || this.tab_button.length === 0) {
        return;
      }

      const firstButton = this.tab_button[0];
      if (firstButton && firstButton.offsetWidth > 0) {
        this.line_bar.style.setProperty(
          "--tab-bar-width",
          `${firstButton.offsetWidth}px`
        );
      }
    } catch (error) {
      console.warn('ProductTabSlider initLineBar error:', error);
    }
  }

  initTabButtons() {
    try {
      if (!this.tab_button || this.tab_button.length === 0) {
        return;
      }

      this.tab_button.forEach((button, index) => {
        if (!button) return;
        
        button.addEventListener("click", (event) => {
          try {
            this.handleTabClick(button, index, event);
          } catch (error) {
            console.warn('ProductTabSlider: Tab click handler error:', error);
          }
        });
      });
    } catch (error) {
      console.warn('ProductTabSlider initTabButtons error:', error);
    }
  }

  handleTabClick(button, index, event) {
    try {
      // 防止默认行为
      if (event) {
        event.preventDefault();
      }

      // 更新线条位置
      if (this.line_bar && button.offsetLeft !== undefined && button.offsetWidth > 0) {
        this.line_bar.style.setProperty(
          "--tab-bar-offset",
          `${button.offsetLeft}px`
        );
        this.line_bar.style.setProperty(
          "--tab-bar-width",
          `${button.offsetWidth}px`
        );
      }

      // 更新按钮状态
      if (this.tab_button) {
        this.tab_button.forEach((btn) => {
          if (btn && btn.classList) {
            btn.classList.remove("active");
          }
        });
      }

      // 更新内容状态
      if (this.product_slider) {
        this.product_slider.forEach((content) => {
          if (content && content.classList) {
            content.classList.remove("active");
          }
        });
      }

      // 激活当前按钮
      if (button && button.classList) {
        button.classList.add("active");
      }

      // 激活对应内容
      const tabId = button.dataset ? button.dataset.tab : null;
      if (tabId) {
        const targetContent = document.querySelector(`[data-tab-content="${tabId}"]`);
        if (targetContent && targetContent.classList) {
          targetContent.classList.add("active");
        }
      }

      // 处理移动端滚动
      this.handleMobileScroll(button);

      // 更新对应的swiper
      this.updateSwiper(index);

    } catch (error) {
      console.warn('ProductTabSlider handleTabClick error:', error);
    }
  }

  handleMobileScroll(button) {
    try {
      const tabNavigation = this.querySelector(".tab-navigation");
      if (!tabNavigation || !button || window.innerWidth >= 750) {
        return;
      }

      const buttonRect = button.getBoundingClientRect();
      const navRect = tabNavigation.getBoundingClientRect();
      
      if (!buttonRect || !navRect) {
        return;
      }

      const scrollLeft = tabNavigation.scrollLeft || 0;
      const buttonLeft = buttonRect.left - navRect.left + scrollLeft;
      const buttonCenter = buttonLeft + buttonRect.width / 2;
      const navCenter = navRect.width / 2;
      const targetScrollLeft = buttonCenter - navCenter;
      const maxScrollLeft = Math.max(0, tabNavigation.scrollWidth - navRect.width);
      const finalScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));

      if (tabNavigation.scrollTo) {
        tabNavigation.scrollTo({
          left: finalScrollLeft,
          behavior: "smooth",
        });
      } else {
        // 兜底方案
        tabNavigation.scrollLeft = finalScrollLeft;
      }
    } catch (error) {
      console.warn('ProductTabSlider handleMobileScroll error:', error);
    }
  }

  updateSwiper(index) {
    try {
      if (this.swipers && this.swipers[index] && typeof this.swipers[index].update === 'function') {
        setTimeout(() => {
          try {
            this.swipers[index].update();
          } catch (error) {
            console.warn('ProductTabSlider: Swiper update error:', error);
          }
        }, 100);
      }
    } catch (error) {
      console.warn('ProductTabSlider updateSwiper error:', error);
    }
  }

  initHoverVideo() {
    try {
      if (this.tabNavigation) {
        this.tabNavigation.addEventListener(
          "mouseenter",
          this.hoverVideoEnter.bind(this)
        );
        this.tabNavigation.addEventListener(
          "mouseleave",
          this.hoverVideoLeave.bind(this)
        );
      }
    } catch (error) {
      console.warn('ProductTabSlider initHoverVideo error:', error);
    }
  }

  hoverVideoEnter() {
    try {
      const videoElement = this.querySelector(".product-video video");
      if (videoElement?.readyState >= 2) {
        videoElement.play().catch((e) => console.warn("ProductTabSlider: Video play error:", e));
      }
    } catch (error) {
      console.warn('ProductTabSlider hoverVideoEnter error:', error);
    }
  }

  hoverVideoLeave() {
    try {
      const videoElement = this.querySelector(".product-video video");
      videoElement?.pause();
    } catch (error) {
      console.warn('ProductTabSlider hoverVideoLeave error:', error);
    }
  }

  initVariantSelectors() {
    try {
      const variantSelectors = this.querySelectorAll('.variant-selector');
      
      if (!variantSelectors || variantSelectors.length === 0) {
        return;
      }
      
      variantSelectors.forEach(selector => {
        if (!selector) return;
        
        selector.addEventListener('change', (e) => {
          try {
            if (e && e.target) {
              this.handleVariantChange(e.target);
            }
          } catch (error) {
            console.warn('ProductTabSlider: Variant change handler error:', error);
          }
        });
      });
    } catch (error) {
      console.warn('ProductTabSlider initVariantSelectors error:', error);
    }
  }

  handleVariantChange(selector) {
    try {
      if (!selector || !selector.options) {
        console.warn('ProductTabSlider: Invalid selector in handleVariantChange');
        return;
      }

      const selectedOption = selector.options[selector.selectedIndex];
      if (!selectedOption) {
        console.warn('ProductTabSlider: No selected option found');
        return;
      }

      const productCard = selector.closest('.product-card') || selector.closest('.ac-product-card');
      if (!productCard) {
        console.warn('ProductTabSlider: Product card not found');
        return;
      }

      const elements = VariantUtils.getProductCardElements(productCard);
      if (!elements) return;
      
      // 更新所有相关元素
      VariantUtils.updatePriceDisplay(elements, selectedOption);
      VariantUtils.updateProductImage(elements.imageElement, selectedOption);
      VariantUtils.updateVariantInput(elements.variantIdInput, selectedOption);

    } catch (error) {
      console.warn('ProductTabSlider handleVariantChange error:', error);
    }
  }



  // 添加销毁方法
  disconnectedCallback() {
    try {
      // 销毁所有Swiper实例
      if (this.swipers && this.swipers.length > 0) {
        this.swipers.forEach(swiper => {
          if (swiper && typeof swiper.destroy === 'function') {
            swiper.destroy(true, true);
          }
        });
        this.swipers = [];
      }
    } catch (error) {
      console.warn('ProductTabSlider disconnectedCallback error:', error);
    }
  }
}

customElements.define("product-tab-slider", ProductTabSlider);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    let button = this.querySelector("button");

    if (!button) button = this.querySelector("[data-button]");
    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this, false)
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        )
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest(".shopify-section").id.replace(
      "shopify-section-",
      ""
    );
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");

    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.documentElement.style.setProperty(
        "--scrollbar-width",
        `${scrollbarWidth}px`
      );
      document.body.classList.add("modal-open");
    }

    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.classList.remove("modal-open");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

// 视频懒加载和交互功能
class ResponsiveVideoManager {
  constructor() {
    this.videos = new Map();
    this.observers = new Map();
    this.init();
  }

  init() {
    // 初始化所有视频容器
    this.initVideoContainers();
    
    // 设置Intersection Observer用于懒加载
    this.setupIntersectionObserver();
    
    // 监听页面可见性变化
    this.setupVisibilityChangeListener();
  }

  initVideoContainers() {
    const videoContainers = document.querySelectorAll('[data-video-container]');
    
    videoContainers.forEach(container => {
      const videoId = container.getAttribute('data-video-container');
      if (!videoId) return;
      
      const videoData = {
        container,
        id: videoId,
        isLoaded: false,
        isPlaying: false,
        isInView: false,
        type: this.getVideoType(container)
      };
      
      this.videos.set(videoId, videoData);
      this.setupVideoContainer(videoData);
    });
  }

  getVideoType(container) {
    if (container.querySelector('.youtube-wrapper')) return 'youtube';
    if (container.querySelector('.vimeo-wrapper')) return 'vimeo';
    if (container.querySelector('.video-element')) return 'native';
    return 'unknown';
  }

  setupVideoContainer(videoData) {
    const { container, type } = videoData;
    
    // 设置播放按钮点击事件
    const playButton = container.querySelector('.play-button');
    if (playButton) {
      playButton.addEventListener('click', () => {
        this.playVideo(videoData);
      });
    }
    
    // 设置封面图片点击事件
    const poster = container.querySelector('.video-poster');
    if (poster) {
      poster.addEventListener('click', () => {
        this.playVideo(videoData);
      });
    }
    
    // 为原生视频设置事件监听
    if (type === 'native') {
      this.setupNativeVideoEvents(videoData);
    }
  }

  setupNativeVideoEvents(videoData) {
    const video = videoData.container.querySelector('.video-element');
    if (!video) return;
    
    video.addEventListener('loadstart', () => {
      this.showLoading(videoData);
    });
    
    video.addEventListener('canplay', () => {
      this.hideLoading(videoData);
      videoData.isLoaded = true;
    });
    
    video.addEventListener('play', () => {
      videoData.isPlaying = true;
    });
    
    video.addEventListener('pause', () => {
      videoData.isPlaying = false;
    });
    
    video.addEventListener('ended', () => {
      videoData.isPlaying = false;
    });
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '50px',
      threshold: 0.1
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const videoId = entry.target.getAttribute('data-video-container');
        const videoData = this.videos.get(videoId);
        
        if (!videoData) return;
        
        if (entry.isIntersecting) {
          videoData.isInView = true;
          this.handleVideoEnterView(videoData);
        } else {
          videoData.isInView = false;
          this.handleVideoLeaveView(videoData);
        }
      });
    }, options);
    
    // 观察所有视频容器
    this.videos.forEach(videoData => {
      observer.observe(videoData.container);
    });
    
    this.observers.set('intersection', observer);
  }

  setupVisibilityChangeListener() {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // 页面隐藏时暂停所有视频
        this.pauseAllVideos();
      }
    });
  }

  handleVideoEnterView(videoData) {
    // 如果视频还未加载且启用了懒加载，先加载视频
    if (!videoData.isLoaded && (videoData.container.classList.contains('responsive-lazy-video') || videoData.container.querySelector('.responsive-lazy-video'))) {
      this.loadVideo(videoData);
      return;
    }
    
    const autoplay = this.shouldAutoplay(videoData);
    if (autoplay && videoData.isLoaded) {
      this.playVideo(videoData);
    }
  }

  handleVideoLeaveView(videoData) {
    // 离开视窗时暂停视频
    if (videoData.isPlaying) {
      this.pauseVideo(videoData);
    }
  }

  shouldAutoplay(videoData) {
    const { container } = videoData;
    
    // 检查是否设置了自动播放
    const video = container.querySelector('video, iframe');
    if (!video) return false;
    
    // 对于原生视频，检查autoplay属性
    if (video.tagName === 'VIDEO') {
      return video.hasAttribute('autoplay');
    }
    
    // 对于iframe，检查src中的autoplay参数
    if (video.tagName === 'IFRAME') {
      const src = video.src || video.getAttribute('data-src') || '';
      return src.includes('autoplay=1');
    }
    
    return false;
  }

  loadVideo(videoData) {
    const { container, type } = videoData;
    
    this.showLoading(videoData);
    
    switch (type) {
      case 'native':
        this.loadNativeVideo(videoData);
        break;
      case 'youtube':
        this.loadYouTubeVideo(videoData);
        break;
      case 'vimeo':
        this.loadVimeoVideo(videoData);
        break;
    }
  }

  loadNativeVideo(videoData) {
    const video = videoData.container.querySelector('.video-element');
    if (!video) return;
    
    const dataSrc = video.getAttribute('data-src');
    if (dataSrc && !video.src) {
      video.src = dataSrc;
      
      const sources = video.querySelectorAll('source[data-src]');
      sources.forEach(source => {
        const srcValue = source.getAttribute('data-src');
        if (srcValue) {
          source.src = srcValue;
          source.removeAttribute('data-src');
        }
      });
      
      video.load();
    }
  }

  loadYouTubeVideo(videoData) {
    const iframe = videoData.container.querySelector('.youtube-iframe');
    if (!iframe) return;
    
    const dataSrc = iframe.getAttribute('data-src');
    if (dataSrc && !iframe.src) {
      iframe.src = dataSrc;
      iframe.addEventListener('load', () => {
        this.hideLoading(videoData);
        videoData.isLoaded = true;
        videoData.container.classList.add('loaded');
      });
    }
  }

  loadVimeoVideo(videoData) {
    const iframe = videoData.container.querySelector('.vimeo-iframe');
    if (!iframe) return;
    
    const dataSrc = iframe.getAttribute('data-src');
    if (dataSrc && !iframe.src) {
      iframe.src = dataSrc;
      iframe.addEventListener('load', () => {
        this.hideLoading(videoData);
        videoData.isLoaded = true;
        videoData.container.classList.add('loaded');
      });
    }
  }

  playVideo(videoData) {
    const { container, type } = videoData;
    
    if (!videoData.isLoaded) {
      this.loadVideo(videoData);
      // 等待加载完成后播放
      setTimeout(() => {
        if (videoData.isLoaded) {
          this.playVideo(videoData);
        }
      }, 1000);
      return;
    }
    
    switch (type) {
      case 'native':
        this.playNativeVideo(videoData);
        break;
      case 'youtube':
        this.playYouTubeVideo(videoData);
        break;
      case 'vimeo':
        this.playVimeoVideo(videoData);
        break;
    }
    
    // 隐藏封面和播放按钮
    container.classList.add('loaded');
  }

  playNativeVideo(videoData) {
    const video = videoData.container.querySelector('.video-element');
    if (video && video.readyState >= 2) {
      video.play().catch(error => {
        console.warn('Native video play error:', error);
      });
    }
  }

  playYouTubeVideo(videoData) {
    const iframe = videoData.container.querySelector('.youtube-iframe');
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        '{"event":"command","func":"playVideo","args":""}',
        '*'
      );
    }
  }

  playVimeoVideo(videoData) {
    const iframe = videoData.container.querySelector('.vimeo-iframe');
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        '{"method":"play"}',
        '*'
      );
    }
  }

  pauseVideo(videoData) {
    const { type } = videoData;
    
    switch (type) {
      case 'native':
        this.pauseNativeVideo(videoData);
        break;
      case 'youtube':
        this.pauseYouTubeVideo(videoData);
        break;
      case 'vimeo':
        this.pauseVimeoVideo(videoData);
        break;
    }
  }

  pauseNativeVideo(videoData) {
    const video = videoData.container.querySelector('.video-element');
    if (video) {
      video.pause();
    }
  }

  pauseYouTubeVideo(videoData) {
    const iframe = videoData.container.querySelector('.youtube-iframe');
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        '{"event":"command","func":"pauseVideo","args":""}',
        '*'
      );
    }
  }

  pauseVimeoVideo(videoData) {
    const iframe = videoData.container.querySelector('.vimeo-iframe');
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        '{"method":"pause"}',
        '*'
      );
    }
  }

  pauseAllVideos() {
    this.videos.forEach(videoData => {
      if (videoData.isPlaying) {
        this.pauseVideo(videoData);
      }
    });
  }

  showLoading(videoData) {
    const loadingElement = videoData.container.querySelector('.video-loading');
    if (loadingElement) {
      loadingElement.style.display = 'flex';
    }
  }

  hideLoading(videoData) {
    const loadingElement = videoData.container.querySelector('.video-loading');
    if (loadingElement) {
      loadingElement.style.display = 'none';
    }
  }

  destroy() {
    this.observers.forEach(observer => {
      observer.disconnect();
    });
    this.observers.clear();
    this.videos.clear();
  }
}

window.ResponsiveVideoManager = ResponsiveVideoManager;